import {useEffect, useRef, useState} from "react";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import Button from "@rio-cloud/rio-uikit/Button";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import ConfirmationDialog from "@rio-cloud/rio-uikit/ConfirmationDialog";
import {formatMaskTime} from "@utils";
import {useSearchParams} from "react-router-dom";
import {ExpanderPanel} from "@rio-cloud/rio-uikit";
import {CheckpointResponse} from "../../../types/checkpoint";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreateCheckpoint: (checkpointData: any) => void;
  checkpointToEdit?: CheckpointResponse | null;
}

const defaultStep = {
  title: "",
  description: "",
  images: [] as File[],
  weight: "",
  volume: "",
  time: "",
  enabledFields: {
    title: true,
    description: true,
    images: true,
    weight: true,
    volume: true,
    time: true,
  },
};

const stepFields: Array<keyof typeof defaultStep.enabledFields> = [
  "description",
  "weight",
  "volume",
  "images",
];

const AddCheckpoint = ({open, onClose, onCreateCheckpoint, checkpointToEdit}: Props) => {
  const [checkpointName, setCheckpointName] = useState("");
  const [steps, setSteps] = useState([structuredClone(defaultStep)]);
  const [currentStep, setCurrentStep] = useState(0);
  const [openCheckpointNameModal, setOpenCheckpointNameModal] = useState(false);
  const [openConfirmCancelModal, setOpenConfirmCancelModal] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const STEP_WIDTH = 416;

  const [searchParams] = useSearchParams();
  const checkpointId = searchParams.get("id");

  const buildCheckpointPayload = (nameOverride?: string) => {
    return {
      name: nameOverride ?? checkpointName,
      stages: steps.map((step) => ({
        name: step.title || null,
        comments: step.description || null,
        plannedTimeMinutes: step.time ? convertTimeToMinutes(step.time) : null,
        fields: buildEnabledFields(step.enabledFields),
        photosBase64: [],
      })),
    };
  };

  const buildEnabledFields = (enabledFields: typeof defaultStep.enabledFields) => {
    const fields: string[] = [];
    if (enabledFields.description) fields.push("description");
    if (enabledFields.weight) fields.push("weight");
    if (enabledFields.volume) fields.push("volume");
    if (enabledFields.images) fields.push("images");
    return fields.length > 0 ? fields : null;
  };

  const convertTimeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return (hours || 0) * 60 + (minutes || 0);
  };

  const handleAddStep = () => {
    setSteps([...steps, structuredClone(defaultStep)]);
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          left: (steps.length) * STEP_WIDTH,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleDeleteStep = (indexToDelete: number) => {
    if (steps.length === 1) return;
    const updatedSteps = steps.filter((_, index) => index !== indexToDelete);
    setSteps(updatedSteps);

    if (currentStep >= updatedSteps.length) {
      setCurrentStep(updatedSteps.length - 1);
    }

    setTimeout(() => {
      scrollToStep(Math.min(currentStep, updatedSteps.length - 1));
    }, 100);
  };

  const getFieldLabel = (field: string) => {
    switch (field) {
      case "title":
        return "Título";
      case "description":
        return "Descrição";
      case "images":
        return "Imagens";
      case "weight":
        return "Peso";
      case "volume":
        return "Volume";
      case "time":
        return "Tempo Planejado";
      default:
        return field;
    }
  };

  const resetForm = () => {
    setCheckpointName("");
    setSteps([structuredClone(defaultStep)]);
    setCurrentStep(0);
    setOpenCheckpointNameModal(false);
    setOpenConfirmCancelModal(false);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const index = Math.round(scrollLeft / STEP_WIDTH);
      if (index >= 0 && index < steps.length) {
        setCurrentStep(index);
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [steps.length]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (open && checkpointToEdit) {
      setCheckpointName(checkpointToEdit.name);

      const filledSteps = checkpointToEdit.stages.map(stage => ({
        title: stage.name ?? "",
        description: stage.comments ?? "",
        images: [],
        weight: "", // mapeie se necessário
        volume: "", // idem
        time: stage.plannedTimeMinutes != null
          ? String(Math.floor(stage.plannedTimeMinutes / 60)).padStart(2, "0") + ":" +
          String(stage.plannedTimeMinutes % 60).padStart(2, "0")
          : "",
        enabledFields: {
          title: true,
          description: stage.fields?.includes("description") ?? false,
          images: stage.fields?.includes("images") ?? false,
          weight: stage.fields?.includes("weight") ?? false,
          volume: stage.fields?.includes("volume") ?? false,
          time: true,
        },
      }));

      setSteps(filledSteps.length ? filledSteps : [structuredClone(defaultStep)]);
    }
  }, [open, checkpointToEdit]);

  const scrollToStep = (index: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: index * STEP_WIDTH,
        behavior: "smooth",
      });
    }
  };

  const handleRequestClose = () => {
    setOpenConfirmCancelModal(true);
    return false;
  };

  return (
    <>
      <Dialog
        show={open}
        title={checkpointToEdit ? "Editar Checkpoint" : "Criar Checkpoint"}
        onClose={handleRequestClose}
        onCloseValidation={handleRequestClose}
        className="checkpointDialog"
        showCloseButton
        bsSize="sm"
        body={
          <div className="display-flex flex-column gap-24">

            <div className="display-flex justify-content-center">
              <h4><strong>Etapa {currentStep + 1}/{steps.length}</strong></h4>
            </div>

            <div
              ref={scrollContainerRef}
              style={{
                display: 'flex',
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                gap: 16,
                paddingBottom: 16,
              }}
            >
              {steps.map((step, index) => (
                <div
                  key={index}
                  style={{
                    minWidth: '100%',
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                    background: '#fff',
                    border: '1px solid #329ca6',
                    borderRadius: 8,
                    padding: 20,
                    boxSizing: 'border-box',
                  }}
                >
                  <div className="display-flex flex-column gap-8">
                    <div className="display-flex justify-content-between align-items-center" style={{marginBottom: 10}}>
                      {checkpointToEdit && (
                        <div>
                          <label htmlFor="checkpointNameEdit">Nome do checklist</label>
                          <input
                            id="checkpointNameEdit"
                            type="text"
                            className="form-control"
                            value={checkpointName}
                            onChange={(e) => setCheckpointName(e.target.value)}
                            placeholder="Digite o nome do checklist"
                            maxLength={50}
                            required
                          />
                        </div>
                      )}
                      <div style={{alignSelf: "flex-end"}}>
                        <Button
                          bsStyle={Button.DANGER}
                          disabled={steps.length === 1}
                          onClick={() => handleDeleteStep(index)} className={"rioglyph rioglyph-trash"}></Button>
                      </div>
                    </div>

                    {(step.enabledFields.title || step.enabledFields.time) && (
                      <div className="display-flex" style={{gap: 16, marginBottom: 10}}>
                        {step.enabledFields.title && (
                          <div style={{flex: 1}}>
                            <label htmlFor={`inputTitle${index}`}>Título da etapa</label>
                            <div className="input-group">
                              <input
                                id={`inputTitle${index}`}
                                type="text"
                                className="simulador-input"
                                value={step.title}
                                onChange={(e) => {
                                  const updatedSteps = [...steps];
                                  updatedSteps[index].title = e.target.value;
                                  setSteps(updatedSteps);
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {step.enabledFields.time && (
                          <div style={{flex: 1}}>
                            <label htmlFor={`inputTime${index}`}>Tempo planejado</label>
                            <div className="input-group">
                              <input
                                id={`inputTime${index}`}
                                type="text"
                                className="simulador-input"
                                value={step.time}
                                onChange={(e) => {
                                  const formattedTime = formatMaskTime(e.target.value);
                                  const updatedSteps = [...steps];
                                  updatedSteps[index].time = formattedTime;
                                  setSteps(updatedSteps);
                                }}
                                maxLength={5}
                                placeholder="00:00"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div
                      className="display-flex flex-wrap"
                    >
                      {stepFields.map((field) => (
                        <div
                          key={field}
                          style={{
                            flex: '0 0 calc(25% - 3px)',
                            maxWidth: 'calc(25% - 3px)',
                          }}
                        >
                          <Checkbox
                            label={`${getFieldLabel(field)}`}
                            checked={step.enabledFields[field]}
                            onChange={() => {
                              const updatedSteps = [...steps];
                              updatedSteps[index].enabledFields[field] = !updatedSteps[index].enabledFields[field];
                              setSteps(updatedSteps);
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    <ExpanderPanel
                      title="Preview da tela da etapa"
                      bsStyle="blank"
                      titleClassName="text-bold"
                      bodyClassName="padding-0"
                      open={true}
                    >
                      <div className="form-group padding-left-0">
                        {step.enabledFields.description && (
                          <div className="">
                            <div className="" style={{marginTop: 10}}>
                              <label htmlFor={`textAreaDescription${index}`}>Descrição (Opcional) </label>
                              <textarea
                                id={`textAreaDescription${index}`}
                                className="form-control"
                                rows={3}
                                value={step.description}
                                onChange={(e) => {
                                  const updatedSteps = [...steps];
                                  updatedSteps[index].description = e.target.value;
                                  setSteps(updatedSteps);
                                }}
                                readOnly
                                disabled
                              />
                            </div>
                          </div>
                        )}
                        {(step.enabledFields.weight || step.enabledFields.volume) && (
                          <div className="display-flex"
                               style={{gap: 16, marginTop: 10}}>
                            {step.enabledFields.weight && (
                              <div style={{flex: '0 0 48%'}}>
                                <label htmlFor={`inputWeight${index}`}>Peso da carga (kg)</label>
                                <div className="input-group">
                                  <input
                                    id={`inputWeight${index}`}
                                    type="text"
                                    className="simulador-input"
                                    style={{width: '100%'}}
                                    value={step.weight}
                                    onChange={(e) => {
                                      const updatedSteps = [...steps];
                                      updatedSteps[index].weight = e.target.value;
                                      setSteps(updatedSteps);
                                    }}
                                    readOnly
                                    disabled
                                  />
                                </div>
                              </div>
                            )}
                            {step.enabledFields.volume && (
                              <div style={{flex: '0 0 48%'}}>
                                <label htmlFor={`inputVolume${index}`}>Volume da carga (m³)</label>
                                <div className="input-group">
                                  <input
                                    id={`inputVolume${index}`}
                                    type="text"
                                    className="simulador-input"
                                    style={{width: '100%'}}
                                    value={step.volume}
                                    onChange={(e) => {
                                      const updatedSteps = [...steps];
                                      updatedSteps[index].volume = e.target.value;
                                      setSteps(updatedSteps);
                                    }}
                                    readOnly
                                    disabled
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {step.enabledFields.images && (
                          <div className="width-80pct" style={{marginTop: 10}}>
                            <label>Carregar imagens</label>
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={(e) => {
                                const updatedSteps = [...steps];
                                updatedSteps[index].images = Array.from(e.target.files || []);
                                setSteps(updatedSteps);
                              }}
                              disabled
                            />
                          </div>
                        )}
                      </div>
                    </ExpanderPanel>
                  </div>
                </div>
              ))}
            </div>
            <div className="display-flex justify-content-between align-items-center gap-16" style={{marginTop: 20}}>

              <Button
                disabled={currentStep === 0}
                onClick={() => scrollToStep(currentStep - 1)}
                className={"rioglyph rioglyph-chevron-left"}
              />

              <div className="display-flex align-items-center" style={{gap: 24}}>
                <Button
                  onClick={handleAddStep}
                  iconName="rioglyph rioglyph-plus"
                >
                  Adicionar Etapa
                </Button>

                <Button
                  bsStyle={Button.PRIMARY}
                  onClick={() => {
                    if (checkpointToEdit) {
                      onCreateCheckpoint(buildCheckpointPayload());
                      onClose();
                    } else {
                      setOpenCheckpointNameModal(true);
                    }
                  }}
                >
                  {checkpointToEdit ? "Editar Checklist" : "Criar Checklist"}
                </Button>
              </div>

              <Button
                disabled={currentStep === steps.length - 1}
                onClick={() => scrollToStep(currentStep + 1)}
                className={"rioglyph rioglyph-chevron-right"}
              />
            </div>
          </div>
        }
      />

      <Dialog
        show={openCheckpointNameModal}
        onClose={() => {
          setOpenCheckpointNameModal(false);
          setCheckpointName("");
        }}
        showCloseButton
        bsSize="md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const payload = buildCheckpointPayload();
            payload.name = checkpointName;
            onCreateCheckpoint(buildCheckpointPayload(checkpointName));
            setOpenCheckpointNameModal(false);
            setCheckpointName("");
            onClose();
          }}
        >
          <Dialog.Title title="Salvar Checklist"/>
          <Dialog.Body>
            <div className="form-group">
              <label htmlFor="checkpointNameInput">Nome do Checklist</label>
              <div className="display-flex align-items-center margin-top-10 margin-bottom-10">
                <span className="rioglyph rioglyph-calendar-today text-size-20 margin-right-10"></span>
                <input
                  id="checkpointNameInput"
                  className="form-control"
                  maxLength={50}
                  value={checkpointName}
                  onChange={(e) => setCheckpointName(e.target.value)}
                  required
                />
              </div>
            </div>
          </Dialog.Body>
          <Dialog.Footer>
            <div className="display-flex gap-10 justify-content-end">
              <Button
                bsStyle="default"
                type="button"
                onClick={() => {
                  setOpenCheckpointNameModal(false);
                  setCheckpointName("");
                }}
              >
                Cancelar
              </Button>
              <Button
                bsStyle="primary"
                type="submit"
                disabled={!checkpointName.trim()}
              >
                Salvar
              </Button>
            </div>
          </Dialog.Footer>
        </form>
      </Dialog>

      <ConfirmationDialog
        show={openConfirmCancelModal}
        title={checkpointToEdit ? "Cancelar Edição?" : "Cancelar criação?"}
        content={checkpointToEdit ? "Tem certeza que deseja cancelar a edição do checkpoint?" : "Tem certeza que deseja cancelar a criação do checkpoint?"}
        onClickConfirm={() => {
          setOpenConfirmCancelModal(false);
          resetForm();
          onClose();
        }}
        onClickCancel={() => {
          setOpenConfirmCancelModal(false);
        }}
        cancelButtonText="Não"
        confirmButtonText="Sim, cancelar"
      />
    </>
  );
};

export default AddCheckpoint;
